/**
 * AcronymTooltip — Renders a dotted-underline acronym with hover tooltip explanation.
 * Usage: <Acronym term="NPS" /> → renders "NPS" with a tooltip showing "Net Promoter Score — ..."
 */
import { cn } from '../../lib/utils';

const GLOSSARY = {
  NPS: { full: 'Net Promoter Score', desc: 'Measures customer loyalty on a scale of -100 to 100' },
  SLA: { full: 'Service Level Agreement', desc: 'Committed response and resolution time for support' },
  MRR: { full: 'Monthly Recurring Revenue', desc: 'Predictable monthly income from subscriptions' },
  ARR: { full: 'Annual Recurring Revenue', desc: 'Yearly subscription revenue' },
  UTM: { full: 'Urchin Tracking Module', desc: 'URL parameters that track campaign traffic sources' },
  CRO: { full: 'Conversion Rate Optimization', desc: 'Improving the percentage of visitors who take action' },
  CRM: { full: 'Customer Relationship Management', desc: 'System to manage contacts, leads, and customer interactions' },
  KPI: { full: 'Key Performance Indicator', desc: 'Measurable value that shows progress toward a goal' },
  ROI: { full: 'Return on Investment', desc: 'Profit relative to the cost of an investment' },
  ROAS: { full: 'Return on Ad Spend', desc: 'Revenue earned per dollar spent on advertising' },
  CTR: { full: 'Click-Through Rate', desc: 'Percentage of people who click after seeing your content' },
  CPC: { full: 'Cost Per Click', desc: 'Amount paid each time someone clicks your ad' },
  CPL: { full: 'Cost Per Lead', desc: 'Amount spent to acquire one new lead' },
  CAC: { full: 'Customer Acquisition Cost', desc: 'Total cost to acquire a new customer' },
  LTV: { full: 'Lifetime Value', desc: 'Total revenue a customer generates over their relationship' },
  CLV: { full: 'Customer Lifetime Value', desc: 'Predicted total revenue from a customer relationship' },
  SEO: { full: 'Search Engine Optimization', desc: 'Improving website visibility in search results' },
  ADA: { full: 'Americans with Disabilities Act', desc: 'Accessibility compliance standards for websites' },
  VIP: { full: 'Very Important Person', desc: 'Top-tier customers with premium benefits' },
  SMS: { full: 'Short Message Service', desc: 'Text messaging for customer communication' },
  TTS: { full: 'Text-to-Speech', desc: 'AI converts written text into spoken audio' },
  AI: { full: 'Artificial Intelligence', desc: 'Technology that automates decisions and content creation' },
  API: { full: 'Application Programming Interface', desc: 'Connection between software systems' },
  QR: { full: 'Quick Response', desc: 'Scannable code that links to digital content' },
  OCR: { full: 'Optical Character Recognition', desc: 'AI reads text from images or scanned documents' },
  CSV: { full: 'Comma-Separated Values', desc: 'Standard file format for spreadsheet data export' },
  PDF: { full: 'Portable Document Format', desc: 'Universal document format for sharing files' },
  SSO: { full: 'Single Sign-On', desc: 'One login to access multiple applications' },
  CSAT: { full: 'Customer Satisfaction Score', desc: 'Direct measure of how happy customers are' },
  OKR: { full: 'Objectives & Key Results', desc: 'Goal-setting framework for tracking outcomes' },
  B2B: { full: 'Business-to-Business', desc: 'Selling products or services to other businesses' },
  B2C: { full: 'Business-to-Consumer', desc: 'Selling directly to individual customers' },
  GDPR: { full: 'General Data Protection Regulation', desc: 'EU privacy and data protection law' },
  HIPAA: { full: 'Health Insurance Portability & Accountability Act', desc: 'US healthcare data privacy standards' },
  OAuth: { full: 'Open Authorization', desc: 'Secure login protocol using third-party accounts' },
  MCC: { full: 'Manager Client Center', desc: 'Google Ads account that manages multiple ad accounts' },
};

export const Acronym = ({ term, className }) => {
  const entry = GLOSSARY[term];
  if (!entry) return <span>{term}</span>;

  return (
    <span
      className={cn(
        "relative group cursor-help border-b border-dotted border-gray-400",
        className
      )}
      data-testid={`acronym-${term.toLowerCase()}`}
    >
      {term}
      <span
        className="absolute z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-150 bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 rounded-lg bg-gray-900 text-white text-[11px] leading-snug whitespace-nowrap shadow-lg pointer-events-none"
        role="tooltip"
      >
        <span className="font-semibold">{entry.full}</span>
        <br />
        <span className="text-gray-300 text-[10px]">{entry.desc}</span>
        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" />
      </span>
    </span>
  );
};

/**
 * Helper: wraps known acronyms in a text string with tooltip components.
 * For use in JSX: <AcronymText text="Improve your NPS score and track SLA compliance" />
 */
export const AcronymText = ({ text, className }) => {
  if (!text) return null;
  const acronymPattern = new RegExp(
    `\\b(${Object.keys(GLOSSARY).join('|')})\\b`,
    'g'
  );
  const parts = text.split(acronymPattern);
  return (
    <span className={className}>
      {parts.map((part, i) =>
        GLOSSARY[part] ? <Acronym key={i} term={part} /> : part
      )}
    </span>
  );
};
